import type {FunctionComponent, SyntheticEvent} from 'react'
import React, {Fragment} from 'react'
import clsx from 'clsx'
import {Disclosure, DisclosureButton, DisclosurePanel} from '@headlessui/react'
import {useOutletContext, useRouteLoaderData, useSubmit} from '@remix-run/react'
import {getTranslation} from '~/localization/translation'
import type {Product, RootAndListContext, RootLoaderResponse, SlugApiResponse} from '~/types'
import {useReportItemSeen, useReportWidgetClick, useSendPartnerProductClickout} from '~/hooks/analytic-hooks'
import {AmazonLogoIcon} from '~/icons/amazon-logo-icon'
import {IconProductBrand} from '~/icons/icon-product-brand'
import {objectToQueryString} from '~/utils/commons'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {IconChevronRightThick} from '~/icons/brg-icon-chevron-right-thick'
import {FeedbackStarSharp} from '~/icons/feedback-star-sharp'
import {IconKeySpec} from '~/icons/icon-key-spec'
import {getDomainConfig} from '~/domains'
import {grantAllConsent} from '~/utils/consent-manager.client'
import CustomerHighlightsBeyond from '~/components/commons/customer-insights-beyond'
import ProductCardBadge from '../badges/card-placement-badge'
import {DiscountBadgeReorganized} from '../badges/discount-badge-reorganized'
import SaleEventBadge from '../badges/saleEventBadge'

export interface TopPicksUpdatedCardProps {
  index: number
  product: Product
}

const TopPicksSpecsCard: FunctionComponent<TopPicksUpdatedCardProps> = ({product, index}) => {
  const {locale, featureFlags, language, trackAnalytics, domain, routeName} = useRouteLoaderData(
    'root',
  ) as RootLoaderResponse
  const {
    clickId,
    setShowCookieConsentGdpr,
    dataResponse: {productsCategory} = {} as SlugApiResponse,
  } = useOutletContext<RootAndListContext>()
  const translations = getTranslation(language)
  const submit = useSubmit()
  const {styles: stylesBydomain, domainName, siteName} = getDomainConfig(domain, locale)

  const {
    name,
    badge,
    asin,
    discount,
    brand,
    image,
    coupon,
    affiliateLink,
    isSellerProduct,
    brief,
    price,
    keySpecs,
    customerHighlights,
    ratingScore,
    keyFeatures,
    numOfRatings,
    chosenBy,
  } = product

  const mergedBriefParts = brief?.length ? brief.join('') : name
  const scoreFormatter = new Intl.NumberFormat(language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })

  const productScore = scoreFormatter.format(product.listScore)
  const affiliateLinkWithClickId = affiliateLink.url.replaceAll(CLICK_ID_PLACEHOLDER, clickId)
  const shouldForceTrackAnalytics = Boolean(
    featureFlags?.cookieConsentVariation === 'opt-in' &&
    (!trackAnalytics?.permission || trackAnalytics?.permission === 'denied'),
  )

  const trackSearchParams = objectToQueryString({
    session_id: trackAnalytics?.session_id,
    ...trackAnalytics?.pageLoadData,
    page_id: trackAnalytics?.page_id,
    user_id: trackAnalytics?.user_id,
  })
  const redirectUrl = shouldForceTrackAnalytics
    ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
    : affiliateLinkWithClickId

  const reportClickout = useSendPartnerProductClickout({
    product,
    index: index + 1,
    locale,
    listName: 'category-products',
    categorySlug: productsCategory?.slug,
    categoryName: productsCategory?.name,
  })
  const reportWidgetClick = useReportWidgetClick()
  const [cardRef] = useReportItemSeen({
    item_list_id: productsCategory?.slug,
    item_name: name,
    item_source: 'list',
    partner_item_brand: brand ?? '',
    partner_item_id: asin,
    affiliation: affiliateLink.type,
    is_dws: isSellerProduct,
    price: price?.amount,
    currency: price?.currency,
    click_element_name: 'top-picks',
    index: index + 1,
  })
  const handleCardClick = (_e: SyntheticEvent) => {
    if (shouldForceTrackAnalytics) {
      setShowCookieConsentGdpr(false)
      grantAllConsent()
      submit(
        {
          'accept-gdpr': 'granted',
          redirect: 'true',
          'track-analytics-data': JSON.stringify({...trackAnalytics, permission: 'granted'}),
        },
        {method: 'POST', action: '/cookie-consent', replace: false, navigate: false},
      )
    }

    reportClickout()
    reportWidgetClick({
      widget_name: 'top-picks-card',
      widget_place: `card-${index + 1}`,
    })

    if (typeof Storage != 'undefined') {
      localStorage.setItem('clickTime', Date.now().toString())
      if (featureFlags?.showHustonProblemPopup) localStorage.setItem('redirectUrl', redirectUrl)
    }
  }

  function handleDisclosureClick(name: 'brief' | 'specs' | 'keyFeatures', isOpen: boolean) {
    reportWidgetClick({
      widget_name: name,
      widget_place: `top-picks-specs-card-${index + 1}`,
      trigger_type: !isOpen ? 'open' : 'close',
    })
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Review',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: `${+productScore}`,
              bestRating: 10,
              worstRating: 0,
            },
            author: {
              '@type': 'Organization',
              name: siteName,
            },
            reviewBody: `${mergedBriefParts}`,
            itemReviewed: {
              '@type': 'Product',
              name: `${name}`,
              review: {
                '@context': 'https://schema.org/',
                '@type': 'Review',
                reviewRating: {
                  '@type': 'Rating',
                  ratingValue: `${+productScore}`,
                  bestRating: 10,
                  worstRating: 0,
                },
                author: {
                  '@type': 'Organization',
                  name: siteName,
                },
                reviewBody: `${mergedBriefParts}`,
              },
            },
          }),
        }}
      ></script>
      <div
        className={clsx(
          'flex flex-col gap-4 transition-all duration-300 ease-in-out',
          'tablet:gap-3 tablet:rounded-2xl tablet:bg-white tablet:px-4 tablet:pb-4 tablet:shadow-md',
          stylesBydomain.LISTPAGE_TOP_PICKS_SPECS_CARD_BORDER,
          'listpage_top_picks_specs_card_border',
        )}
      >
        <ProductCardBadge
          className={clsx(
            'z-40 flex h-full w-fit items-center justify-center overflow-hidden rounded-b-lg rounded-t-lg tablet:rounded-b-lg tablet:rounded-t-none',
            '[&>div>p]:px-3 [&>div>span]:text-xl [&>div>span]:font-semibold',
          )}
          index={index}
          badge={badge}
          coupon={coupon}
        />
        {/* ------------------------------------------- Score Mobile --------------------------------------------- */}

        <a
          href={redirectUrl}
          onClick={handleCardClick}
          aria-label={`view ${brand}`}
          target="_blank"
          rel="sponsored noreferrer"
          className="flex flex-row items-center gap-2 desktop:hidden"
        >
          <span className="text-base font-semibold tracking-wide">
            {productScore}
            <em className="font-normal not-italic"> {translations.ProductCard.scoreSuffix}</em>
          </span>
          {ratingScore
            ? Array.from({length: 5}, (_, i) => {
                const isFilledStar = i < ratingScore
                return (
                  <Fragment key={`${ratingScore}-${i}`}>
                    <div className="flex flex-row items-center gap-1">
                      <FeedbackStarSharp
                        className={clsx(
                          'size-5 stroke-[#FBBF24]',
                          isFilledStar ? 'fill-[#FBBF24]' : 'fill-transparent',
                        )}
                      />
                    </div>
                  </Fragment>
                )
              })
            : null}
        </a>

        <h3 className="line-clamp-2 text-[20px] font-normal capitalize text-[#363636] tablet:text-base tablet:font-semibold">
          {name}
        </h3>

        {/* -------------------------------------------  INFO CARD   --------------------------------------------- */}

        <div
          ref={cardRef}
          className={clsx(
            'border-gray relative flex h-full min-h-[300px] w-full flex-row rounded-3xl border bg-white shadow-product-page-section',
            'tablet:min-h-fit tablet:flex-col tablet:items-center tablet:gap-3 tablet:rounded-xl',
            'tablet:border-none tablet:shadow-none',
          )}
        >
          {/* ------------------------------------------- Discount Badge Desktop --------------------------------------------- */}

          {discount ? (
            <DiscountBadgeReorganized
              className={clsx(
                'absolute right-0 top-8 z-10 flex h-10 min-w-28 max-w-fit items-center justify-center rounded-l-3xl px-2 [&>p]:text-[18px]',
                'tablet:top-3 tablet:h-8 tablet:min-w-20 tablet:[&>p]:text-sm',
                'tablet:hidden',
                stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_BG,
                stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_TEXT,
                'listpage_badge_card_discount_bg listpage_badge_card_discount_text',
              )}
              discount={discount}
            />
          ) : null}

          {/* ------------------------------------------- Image + Brand --------------------------------------------- */}

          <div className="relative flex w-full flex-col items-center justify-center tablet:w-fit tablet:gap-1">
            <img src={image} alt={name} className="size-56 object-scale-down tablet:h-32 tablet:w-36" />
            <a
              href={redirectUrl}
              onClick={handleCardClick}
              aria-label={`view ${brand}`}
              target="_blank"
              rel="sponsored noreferrer"
              className="flex flex-row items-center gap-2"
            >
              <IconProductBrand className="size- tablet:size-5" />
              <p
                className="line-clamp-1 text-2xl font-normal capitalize tablet:text-base tablet:font-medium"
                data-testid="category-card-brand"
              >
                {brand}
              </p>
            </a>
          </div>

          <div className="hidden w-full flex-row items-center justify-between tablet:flex">
            <AmazonLogoIcon className="mt-1 h-6 w-[58px] shrink-0" />

            {/* ------------------------------------------- Discount Badge Mobile --------------------------------------------- */}
            {discount ? (
              <DiscountBadgeReorganized
                className={clsx(
                  'h-10 min-w-28 max-w-fit items-center justify-center rounded-full px-2 tablet:flex [&>p]:text-[18px]',
                  'tablet:top-3 tablet:h-8 tablet:min-w-20 tablet:[&>p]:text-sm',
                  'hidden',
                  stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_BG,
                  stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_TEXT,
                  'listpage_badge_card_discount_bg listpage_badge_card_discount_text',
                )}
                discount={discount}
              />
            ) : null}
          </div>
          <div
            className={clsx(
              'ml-auto flex w-56 min-w-74 flex-col items-center justify-center gap-6 border-l border-l-gray-400 px-8',
              'tablet:ml-0 tablet:w-full tablet:min-w-fit tablet:gap-1 tablet:border-none tablet:px-0',
            )}
          >
            {/* ------------------------------------------- Score Desktop --------------------------------------------- */}

            <a
              href={redirectUrl}
              onClick={handleCardClick}
              aria-label={`view ${brand}`}
              target="_blank"
              rel="sponsored noreferrer"
              className="flex flex-row items-center gap-2 tablet:hidden"
            >
              <p className="text-2xl font-light">
                {productScore}
                {translations.ProductCard.scoreSuffix}
              </p>
              {ratingScore
                ? Array.from({length: 5}, (_, i) => {
                    const isFilledStar = i < ratingScore
                    return (
                      <Fragment key={`${ratingScore}-${i}`}>
                        <div className="flex flex-row items-center gap-1">
                          <FeedbackStarSharp
                            className={clsx(
                              'size-6 stroke-[#FBBF24]',
                              isFilledStar ? 'fill-[#FBBF24]' : 'fill-transparent',
                            )}
                          />
                        </div>
                      </Fragment>
                    )
                  })
                : null}
            </a>

            {/* -------------------------------------------  CTA  --------------------------------------------- */}

            <div className="flex w-full flex-col gap-3">
              <a
                href={redirectUrl}
                onClick={handleCardClick}
                aria-label={`view ${brand}`}
                target="_blank"
                rel="sponsored noreferrer"
                className={clsx(
                  'relative flex h-[50px] w-full flex-row items-center justify-center gap-8 rounded-3xl px-3',
                  stylesBydomain.LISTPAGE_BUTTON_NEW_LAYOUT_PRIMARY,
                  'tablet:h-11',
                  'listpage_button_new_layout_primary',
                )}
              >
                <div
                  className={clsx(
                    'absolute left-3 top-[13px] h-[25px] shrink-0 items-center justify-start rounded-2xl px-2 py-0.5',
                    stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_BG,
                    stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_ICON,
                    stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_TEXT,
                    'tablet:hidden',
                    'listpage_badge_cta_marketplace_logo_bg listpage_badge_cta_marketplace_logo_text listpage_badge_cta_marketplace_logo_icon',
                  )}
                >
                  <AmazonLogoIcon
                    className={clsx(
                      'mt-1 h-4 shrink-0',
                      domainName === 'bestestaubsauger' ||
                        (domainName === 'bestexpertschoice' && locale.toLowerCase() === 'ca')
                        ? 'w-14'
                        : 'w-11',
                    )}
                    domainName={domainName}
                  />
                </div>
                <p className="ml-2 text-base font-semibold capitalize">{translations.ProductCard.checkPrice}</p>
              </a>
              {!!discount && (
                <SaleEventBadge
                  redirectUrl={redirectUrl}
                  handleCardClick={handleCardClick}
                  brand={brand ?? ''}
                  index={index}
                  placement="top-picks-card"
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex w-full flex-col">
          {/* -------------------------------------------Brief --------------------------------------------- */}

          {brief && !!brief.length ? (
            <Disclosure>
              {({open}) => (
                <>
                  <div className="mt-2 flex w-full flex-col gap-7 tablet:mt-0">
                    {open ? (
                      brief?.map((breafPart, i) => (
                        <Fragment key={`${breafPart.slice(0, 10)}-${i}`}>
                          <p className={clsx('text-lg font-light text-carbon-900 tablet:text-base')}>{breafPart}</p>
                        </Fragment>
                      ))
                    ) : (
                      <p
                        className={clsx(
                          'line-clamp-4 text-lg font-light text-carbon-900 tablet:line-clamp-[15] tablet:text-base',
                        )}
                      >
                        {brief[0]}
                      </p>
                    )}
                  </div>
                  <DisclosureButton
                    className="mt-5 flex w-full items-center gap-2 border-b border-b-[#94A3B8] py-4 text-[18px]"
                    onClick={() => {
                      handleDisclosureClick('brief', open)
                    }}
                  >
                    {!open ? translations.TopPicks.brief.readMore(open) : null}

                    <IconChevronRightThick
                      className={clsx(
                        'ml-auto size-5 fill-neutral-800 transition-all duration-200 ease-in-out',
                        open ? '-rotate-90' : 'rotate-90',
                      )}
                    />
                  </DisclosureButton>
                </>
              )}
            </Disclosure>
          ) : null}

          {/* -------------------------------------------Specs --------------------------------------------- */}

          {keySpecs && !!keySpecs.length ? (
            <Disclosure>
              {({open}) => (
                <>
                  <DisclosureButton
                    className="mt-5 flex w-full items-center gap-2 border-b border-b-[#94A3B8] py-4 text-[18px]"
                    onClick={() => {
                      handleDisclosureClick('specs', open)
                    }}
                  >
                    {translations.TopPicks.specifications.title}
                    <IconChevronRightThick
                      className={clsx(
                        'ml-auto size-5 fill-neutral-800 transition-all duration-200 ease-in-out',
                        open ? '-rotate-90' : 'rotate-90',
                      )}
                    />
                  </DisclosureButton>
                  <DisclosurePanel>
                    <div className="flex w-full flex-col gap-3 bg-[#F8FAFC] p-4">
                      {keySpecs.map(({name, value}, index) => (
                        <Fragment key={`${name}-${index}`}>
                          <div className="flex flex-row items-start gap-1 text-base">
                            <IconKeySpec
                              className={clsx(
                                'mt-1 shrink-0 tablet:mt-0.5',
                                stylesBydomain.LISTPAGE_WIDGETS_SPECS_CHECKED_ICON,
                                'listpage_widgets_specs_checked_icon',
                              )}
                            />
                            <span className="font-semibold">
                              {name}: <em className="font-light not-italic">{value}</em>
                            </span>
                          </div>
                        </Fragment>
                      ))}
                    </div>
                  </DisclosurePanel>
                </>
              )}
            </Disclosure>
          ) : null}
          {/* -------------------------------------------Key Features --------------------------------------------- */}

          {keyFeatures && !!keyFeatures.length ? (
            <Disclosure>
              {({open}) => (
                <>
                  <DisclosureButton
                    className="mt-5 flex w-full items-center gap-2 border-b border-b-[#94A3B8] py-4 text-[18px]"
                    onClick={() => {
                      handleDisclosureClick('keyFeatures', open)
                    }}
                  >
                    {translations.TopPicks.keyFeatures.title}
                    <IconChevronRightThick
                      className={clsx(
                        'ml-auto size-5 fill-neutral-800 transition-all duration-200 ease-in-out',
                        open ? '-rotate-90' : 'rotate-90',
                      )}
                    />
                  </DisclosureButton>
                  <DisclosurePanel>
                    <div className="flex w-full flex-col gap-3 bg-[#F8FAFC] p-4">
                      {keyFeatures.map(({feature, description}, index) => (
                        <Fragment key={`${name}-${index}`}>
                          <div className="flex flex-row items-start gap-1 text-base">
                            <span className="font-semibold">
                              {feature}: <em className="font-light not-italic">{description}</em>
                            </span>
                          </div>
                        </Fragment>
                      ))}
                    </div>
                    <div className="flex w-64 flex-col gap-3 tablet:w-full">
                      <a
                        href={redirectUrl}
                        onClick={handleCardClick}
                        aria-label={`view ${brand}`}
                        target="_blank"
                        rel="sponsored noreferrer"
                        className={clsx(
                          'relative mt-5 flex h-[50px] w-full flex-row items-center justify-center gap-8 rounded-3xl px-3',
                          stylesBydomain.LISTPAGE_BUTTON_NEW_LAYOUT_PRIMARY,
                          'tablet:h-11',
                          'listpage_button_new_layout_primary',
                        )}
                      >
                        <div
                          className={clsx(
                            'absolute left-3 top-[13px] h-[25px] shrink-0 items-center justify-start rounded-2xl px-2 py-0.5',
                            stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_BG,
                            stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_ICON,
                            stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_TEXT,
                            'tablet:hidden',
                            'listpage_badge_cta_marketplace_logo_bg listpage_badge_cta_marketplace_logo_text listpage_badge_cta_marketplace_logo_icon',
                          )}
                        >
                          <AmazonLogoIcon
                            className={clsx(
                              'mt-1 h-4 shrink-0',
                              domainName === 'bestestaubsauger' ||
                                (domainName === 'bestexpertschoice' && locale.toLowerCase() === 'ca')
                                ? 'w-14'
                                : 'w-11',
                            )}
                            domainName={domainName}
                          />
                        </div>
                        <p className="text-base font-semibold capitalize tablet:hidden">
                          {translations.ProductCard.checkPrice}
                        </p>
                        <p className="hidden text-base font-semibold capitalize tablet:block">
                          {translations.ProductCard.ctaTextView}
                        </p>
                      </a>
                    </div>
                  </DisclosurePanel>
                </>
              )}
            </Disclosure>
          ) : null}
        </div>
        {/* -------------------------------------------  CUSTOMER HIGHLIGHTS Mobile --------------------------------------------- */}
        <CustomerHighlightsBeyond
          className={clsx(
            'mt-5 hidden flex-col gap-1 overflow-hidden transition-all duration-300 ease-in-out tablet:flex',
          )}
          redirectUrl={redirectUrl}
          handleCardClick={handleCardClick}
          brand={brand}
          ratingCount={numOfRatings ?? undefined}
          score={productScore}
          showBottomCta={routeName !== 'product_page'}
          chosenBy={index === 0 ? chosenBy : undefined}
          cardPlacement={index + 1}
          customerHighlights={customerHighlights}
        />
      </div>

      {/* -------------------------------------------  CUSTOMER HIGHLIGHTS Desktop --------------------------------------------- */}
      <CustomerHighlightsBeyond
        className="flex flex-col gap-1 transition-all duration-300 ease-in-out tablet:hidden"
        redirectUrl={redirectUrl}
        handleCardClick={handleCardClick}
        brand={brand}
        ratingCount={numOfRatings ?? undefined}
        score={productScore}
        showBottomCta={routeName !== 'product_page'}
        chosenBy={index === 0 ? chosenBy : undefined}
        cardPlacement={index + 1}
        customerHighlights={customerHighlights}
      />
      <div className="mt-5 border-b border-gray-300 tablet:hidden" />
    </>
  )
}

export default TopPicksSpecsCard
